import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import { cn } from '@/lib/utils'

interface NavItem {
    label: string
    href: string
}

const NAV_ITEMS: NavItem[] = [
    { label: 'Wall', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' }
]

interface NavbarProps {
    /** Set on the immersive home wall so the bar floats without its own surface. */
    floating?: boolean
}

export const Navbar: FC<NavbarProps> = ({ floating = false }): ReactNode => {
    const router = useRouter()

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50',
                !floating &&
                    'border-b border-border/60 bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60'
            )}
        >
            {/* On the wall the bar has no surface of its own — a soft top scrim keeps
                the dark links legible as photos drift underneath. */}
            {floating && (
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-28"
                    style={{
                        background:
                            'linear-gradient(to bottom, rgb(var(--background-rgb) / 0.9) 0%, rgb(var(--background-rgb) / 0.55) 45%, transparent 100%)'
                    }}
                />
            )}

            <nav
                aria-label="Primary"
                className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-7"
            >
                <Link
                    href="/"
                    className="text-lg leading-none"
                    style={{
                        fontFamily: 'var(--font-playfair), Georgia, serif',
                        letterSpacing: '-0.01em',
                        color: 'var(--foreground)'
                    }}
                    aria-label="Moments — home"
                >
                    Moments
                </Link>

                <ul className="flex items-center gap-6 sm:gap-8">
                    {NAV_ITEMS.map((item) => {
                        const isActive =
                            item.href === '/'
                                ? router.pathname === '/'
                                : router.pathname === item.href ||
                                  router.pathname.startsWith(`${item.href}/`)

                        return (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={cn(
                                        'text-[11px] uppercase tracking-[0.2em] transition-opacity',
                                        isActive ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                                    )}
                                    style={{ color: 'var(--foreground)' }}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </nav>
        </header>
    )
}
